/* eslint-disable max-lines-per-function */
/* eslint-disable no-irregular-whitespace */
import { bytesToNDigits } from '@mail/cross-sizes-utils';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_FAMILY_USER, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { isFamilySubsAvailable } from 'reactApp/appHelpers/featuresHelpers';
import {
    overquotaCommunicationPayPlacement,
    overquotaFileLoadConfig,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { isPurchaseDisabled } from 'reactApp/appHelpers/featuresHelpers/features/superAppAuthorizationInfo';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { askUserForPaymentFiles } from 'reactApp/modules/uploadList/oldUploadListBridge';
import type { IInputFile } from 'reactApp/modules/uploadList/uploadList.model';
import { updateUser } from 'reactApp/modules/user/user.thunkActions';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { goToPromoQuota } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { ESplashScreenAction, sendSplashScreenAnalytics } from 'reactApp/ui/Mobile/SplashScreen/SplashScreen.analytics';
import { truncateTextMiddle } from 'reactApp/utils/textHelpers';
import { EQueryParams } from 'server/helpers/getRequestParams';

import styles from './PopupWarnings.css';

interface IProps {
    file: IInputFile;
    isStrangerOwner: boolean;
}

const sendAnalytics = ({ action }: { action: ESplashScreenAction }) => {
    sendSplashScreenAnalytics({
        action,
        name: 'load_file_overquota',
        dwh_enabled_fields: [['name', 'platform']],
    });
};

const OverQuota = ({ file, isStrangerOwner }: IProps) => {
    const dispatch = useDispatch();

    const storage = useSelector(getCurrentStorage);
    const isInlineIntegration = storage === EStorageType.inlineIntegration;
    const fromDeeplink = getQueryParams()[EQueryParams.fromDeeplink];
    const isSuperAppWebView = IS_WEBVIEW && fromDeeplink;
    const isDisabledPurchase = isSuperAppWebView && isPurchaseDisabled();
    const isOverquotaBySpace = useSelector(UserQuotaSelectors.isOverquota);
    const isOverquotaBySpaceAndApi = useSelector(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const overquotaAmount = useSelector(UserQuotaSelectors.getOverQuota);
    const { remaining, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);

    const hasClearButton = overquotaFileLoadConfig.clearButton;

    useEffect(() => {
        sendAnalytics({ action: ESplashScreenAction.show });
        if (!isStrangerOwner && IS_FAMILY_USER && isFamilySubsAvailable) {
            dispatch(updateUser());
        }
    }, []);

    const renderHeader = useCallback(() => {
        if (isStrangerOwner) {
            return <div>Невозможно загрузить файл «{truncateTextMiddle(file.name, 22)}»</div>;
        }

        return <div>Загрузить файл «{truncateTextMiddle(file.name, 22)}» пока нельзя</div>;
    }, [file.name, isStrangerOwner]);

    const content = useMemo(() => {
        if (isStrangerOwner) {
            return 'Загрузка невозможна. У владельца этой папки недостаточно места в Облаке для ваших файлов';
        }

        if (isOverquotaBySpace) {
            return (
                <>
                    Пространство Облака и Почты переполнено
                    <br />
                    на <span>{overquotaAmount.value}</span> — увеличьте с подпиской Mail Space
                    {hasClearButton ? ' или удалите лишнее' : ''}, чтобы загрузить файл
                </>
            );
        }

        const fileOverflow = file.size && bytesToNDigits(file.size - remaining?.original, 3);

        if (fileOverflow && !isOverquotaBySpace) {
            const remainingString =
                remaining?.units === userTotalSpace?.units
                    ? `${remaining?.space} из ${userTotalSpace?.value}`
                    : `${remaining?.value} из ${userTotalSpace?.value}`;
            return (
                <>
                    В пространстве Облака и Почты свободно {remainingString}, файл весит на 
                    <span>{fileOverflow.value}</span> больше. Увеличьте место с подпиской Mail Space, чтобы загрузить его
                </>
            );
        }

        return 'Пространство Облака и Почты переполнено. Удалите лишнее или увеличьте его размер';
    }, [isStrangerOwner, isOverquotaBySpace, overquotaAmount, hasClearButton, remaining, userTotalSpace, file]);

    const successButtonText = useMemo(() => {
        if (isStrangerOwner) {
            return 'Посмотреть тарифы';
        }

        if (isOverquotaBySpace) {
            return 'Снять ограничение';
        }

        return 'Посмотреть тарифы';
    }, [isStrangerOwner, isOverquotaBySpace]);

    const buttons = useMemo(() => {
        if (isInlineIntegration || isDisabledPurchase) {
            return [EButtonType.cancel];
        }

        if (isOverquotaBySpaceAndApi && hasClearButton) {
            return [EButtonType.accept, EButtonType.action];
        }

        return [EButtonType.accept, EButtonType.cancel];
    }, [isInlineIntegration, isDisabledPurchase, isOverquotaBySpaceAndApi, hasClearButton]);

    const { showPaywall } = useBuyClickHandlers({ paySource: 'mnz_overquota_file_upload' });

    const handleConfirm = useCallback(() => {
        sendAnalytics({ action: ESplashScreenAction.click });

        if (isOverquotaBySpace) {
            if (overquotaCommunicationPayPlacement === 'paywall') {
                showPaywall();
            } else {
                goToPromoQuota({ hash: 'tariffs' });
            }
        } else {
            askUserForPaymentFiles('payment', file.descriptorId);
        }
    }, []);

    const handleCancel = useCallback(() => {
        sendAnalytics({ action: ESplashScreenAction.skip });
        askUserForPaymentFiles('skip', file.descriptorId);
    }, []);

    return (
        <BaseConfirmDialog
            className={styles.root}
            dataQAId="overquota-dialog"
            renderHeader={renderHeader}
            renderContent={() => (
                <div className={styles.content}>
                    <div>{content}</div>
                </div>
            )}
            buttons={buttons}
            smallButtons
            buttonTheme="octavius"
            successButtonText={successButtonText}
            onSuccess={handleConfirm}
            cancelButtonText={'Пропустить'}
            onClose={handleCancel}
            actionButtonText="Удалить лишнее"
            onAction={() => {
                goToPromoQuota({ hash: 'cleaner' });
            }}
        />
    );
};

export const OverQuotaComponent = memo(OverQuota);
